import { TRIAL_MOCK_IDS } from '../constants';
import {
  ALL_BRANCHES,
  filterTransactionsInRange,
  getGroupsForBranch,
  getMembersForBranch,
  getTransactionsForBranch
} from './generator';

type DateField = 'due_at' | 'paid_at';
type MockTransaction = ReturnType<typeof getTransactionsForBranch>[number];

const parseDMY = (value: string) => {
  const [d, m, y] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const toDateKey = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const sumPaid = (transactions: MockTransaction[]) =>
  transactions.reduce((sum, t) => sum + Number(t.total_paid_amount), 0);

const sumDue = (transactions: MockTransaction[]) =>
  transactions
    .filter((t) => t.status === 'processing')
    .reduce(
      (sum, t) => sum + (Number(t.amount) - Number(t.total_paid_amount)),
      0
    );

const sumUnsettled = (transactions: MockTransaction[]) =>
  transactions
    .filter((t) => t.status === 'success' && t.settlement_status !== 'settled')
    .reduce((sum, t) => sum + Number(t.total_paid_amount), 0);

const getGrowthPercentage = (current: number, previous: number) => {
  if (previous > 0) {
    return Math.round(((current - previous) / previous) * 100);
  }
  return current > 0 ? 100 : 0;
};

export const getPaymentOverviewMock = (
  timeFilterType: string,
  startDate: string,
  endDate: string,
  branchId?: string
) => {
  const dateField: DateField =
    timeFilterType === 'paid_at' ? 'paid_at' : 'due_at';
  const start = parseDMY(startDate);
  const end = parseDMY(endDate);

  const inRange = filterTransactionsInRange(branchId, start, end, dateField);

  const periodMs = end.getTime() - start.getTime();
  const prevEnd = new Date(start.getTime() - 86400000);
  const prevStart = new Date(prevEnd.getTime() - periodMs);
  const prevRange = filterTransactionsInRange(
    branchId,
    prevStart,
    prevEnd,
    dateField
  );

  const collectionAmount = sumPaid(
    inRange.filter((t) => t.status === 'success')
  );
  const prevCollectionAmount = sumPaid(
    prevRange.filter((t) => t.status === 'success')
  );
  const collectionGrowth = getGrowthPercentage(
    collectionAmount,
    prevCollectionAmount
  );

  return {
    collection_amount: collectionAmount,
    collection_growth: collectionGrowth,
    due_amount: sumDue(inRange),
    to_be_settled: sumUnsettled(inRange),
    groups: getGroupsForBranch(branchId).length,
    members: getMembersForBranch(branchId).length
  };
};

export const getBranchRevenueMock = (
  startDate: string,
  endDate: string,
  branchIdsParam?: string
) => {
  const start = parseDMY(startDate);
  const end = parseDMY(endDate);
  const requestedIds = branchIdsParam
    ? branchIdsParam.split(',').filter(Boolean)
    : [];
  const branches = requestedIds.length
    ? ALL_BRANCHES.filter((b) => requestedIds.includes(b.id))
    : ALL_BRANCHES;

  return branches.map((branch) => {
    const branchTransactions = getTransactionsForBranch(branch.id);
    const revenue: { date: string; collected: number; due: number }[] = [];

    const current = new Date(start);
    while (current <= end) {
      const dateKey = toDateKey(current);
      const collected = branchTransactions
        .filter(
          (t: any) =>
            t.status === 'success' &&
            t.paid_at &&
            toDateKey(new Date(t.paid_at)) === dateKey
        )
        .reduce((sum, t) => sum + Number(t.total_paid_amount), 0);
      const due = branchTransactions
        .filter(
          (t: any) =>
            t.status === 'processing' &&
            toDateKey(new Date(t.due_at)) === dateKey
        )
        .reduce(
          (sum, t) => sum + (Number(t.amount) - Number(t.total_paid_amount)),
          0
        );
      revenue.push({ date: dateKey, collected, due });
      current.setDate(current.getDate() + 1);
    }

    return {
      branch_id: branch.id,
      branch_name: branch.name,
      branch_revenue: revenue
    };
  });
};

export const getGroupPerformanceMock = (
  page: number,
  timeFilterType: string,
  startDate: string,
  endDate: string,
  branchId?: string
) => {
  const dateField: DateField =
    timeFilterType === 'paid_at' ? 'paid_at' : 'due_at';
  const start = parseDMY(startDate);
  const end = parseDMY(endDate);
  const groups = getGroupsForBranch(branchId);

  const allGroups = groups.map((group) => {
    const groupTransactions = filterTransactionsInRange(
      group.branch_id,
      start,
      end,
      dateField
    ).filter((t) => t.group_id === group.id);

    return {
      group_id: group.id,
      group_name: group.name,
      branch_id: group.branch_id,
      branch_name: group.branch_name,
      amount_collected: String(
        sumPaid(groupTransactions.filter((t) => t.status === 'success'))
      ),
      amount_due: String(sumDue(groupTransactions))
    };
  });

  const limit = 5;
  const pageStart = page * limit;
  const paginatedData = allGroups.slice(pageStart, pageStart + limit);

  return {
    data: paginatedData.length > 0 ? paginatedData : null,
    pagination: {
      total_rows: allGroups.length,
      page,
      limit
    }
  };
};

export const getPaymentLinksAndModesMock = (
  timeFilterType: string,
  startDate: string,
  endDate: string,
  branchId?: string
) => {
  const dateField: DateField =
    timeFilterType === 'paid_at' ? 'paid_at' : 'due_at';
  const start = parseDMY(startDate);
  const end = parseDMY(endDate);
  const inRange = filterTransactionsInRange(branchId, start, end, dateField);

  const modeTotals = {
    upi: 0,
    cards: 0,
    card_emi: 0,
    net_banking: 0,
    online_mandates: 0,
    others: 0,
    offline: 0
  };

  inRange
    .filter((t) => t.status === 'success')
    .forEach((t) => {
      const amount = Number(t.total_paid_amount);
      if (t.payment_method === 'cards') {
        modeTotals.cards += amount;
      } else if (t.payment_method === 'card_emi') {
        modeTotals.card_emi += amount;
      } else if (t.payment_method === 'net_banking') {
        modeTotals.net_banking += amount;
      } else if (t.payment_method === 'offline') {
        modeTotals.offline += amount;
      } else if (t.payment_method === 'upi') {
        modeTotals.upi += amount;
      } else {
        modeTotals.others += amount;
      }
    });

  return {
    payment_links: {
      generated: inRange.length,
      converted: inRange.filter((t) => t.status === 'success').length
    },
    payment_modes: modeTotals
  };
};

const DAILY_MOCK_DAYS = 14;

const getMockDayRange = (daysAgo: number) => {
  const dayStart = new Date();
  dayStart.setDate(dayStart.getDate() - daysAgo);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(23, 59, 59, 999);
  return { dayStart, dayEnd, day: `${toDateKey(dayStart)}T00:00:00Z` };
};

const sumByMethod = (transactions: MockTransaction[], method: string) =>
  String(
    transactions
      .filter((t) => t.payment_method === method)
      .reduce(
        (sum, t) => sum + (Number(t.total_paid_amount) || Number(t.amount)),
        0
      )
  );

export const getPaymentVolumeMock = (branchId?: string) => {
  const data = [];

  for (let i = DAILY_MOCK_DAYS - 1; i >= 0; i -= 1) {
    const { dayStart, dayEnd, day } = getMockDayRange(i);
    const dayTransactions = filterTransactionsInRange(
      branchId,
      dayStart,
      dayEnd,
      'due_at'
    ).filter((t) => t.status === 'success' || t.status === 'processing');
    const online = dayTransactions.filter(
      (t) => t.payment_mode === 'automatic'
    );
    const offline = dayTransactions.filter(
      (t) => t.payment_mode !== 'automatic'
    );

    data.push({
      day,
      online_payment_breakdown: {
        upi: sumByMethod(online, 'upi'),
        upi_credit_card: '0',
        cards: sumByMethod(online, 'cards'),
        net_banking: sumByMethod(online, 'net_banking'),
        mandates: '0',
        others: '0'
      },
      offline_payment_breakdown: {
        upi: sumByMethod(offline, 'upi'),
        bank: sumByMethod(offline, 'net_banking'),
        cash: sumByMethod(offline, 'offline'),
        cheque: '0'
      }
    });
  }

  return { data };
};

const MOCK_SETTLEMENT_ACCOUNTS = [
  { bank_name: 'HDFC Bank', bank_account_number: '********6483' },
  { bank_name: 'SBI Bank', bank_account_number: '********1234' }
];

export const getSettlementBreakdownMock = (branchId?: string) => {
  const settled = getTransactionsForBranch(branchId).filter(
    (t) => t.settlement_status === 'settled' && t.settled_at
  );
  const data = [];

  for (let i = DAILY_MOCK_DAYS - 1; i >= 0; i -= 1) {
    const { dayStart, dayEnd, day } = getMockDayRange(i);
    const daySettled = settled.filter((t) => {
      const settledAt = new Date(t.settled_at);
      return settledAt >= dayStart && settledAt <= dayEnd;
    });
    const accounts = MOCK_SETTLEMENT_ACCOUNTS.map((account, index) => ({
      ...account,
      bank_account_name: TRIAL_MOCK_IDS.ORG_NAME,
      bank_nick_name: '',
      amount_settled: String(
        daySettled
          .filter((_, txIndex) => txIndex % 2 === index)
          .reduce((sum, t) => sum + Number(t.total_settled_amount), 0)
      )
    })).filter((account) => Number(account.amount_settled) > 0);
    data.push({ day, accounts });
  }

  return { data };
};
